<template>
	<div class="display-flex gap-100 p-100 asset-element-card-desktop">
		<img
			v-if="thumbnailPhoto"
			:src="thumbnailPhoto"
			alt=""
			role="presentation"
			:class="['img-rounded', imageClass]"
		/>
		<div class="display-flex justify-content-space-between align-items-center gap-25 width-100">
			<div class="asset-element-card__content">
				<div class="display-flex flex-direction-column gap-100">
					<span class="heading-6 ellipsis">{{ name }}</span>
					<div class="display-flex gap-200">
						<div
							v-for="(item, index) in contentItems"
							:key="index"
							:class="['asset-element-card__content-item', item.classes ?? '']"
						>
							<div class="body-sm text-color-content-secondary no-wrap">{{ item.label }}</div>
							<div class="font-weight-bold">{{ item.value }}</div>
						</div>
					</div>
				</div>
			</div>
			<BaseSvgIcon
				v-if="showChevron"
				role="presentation"
				class="arrow"
				name="chevron"
				dir="right"
				width="24"
				height="24"
			/>
		</div>
	</div>
</template>

<script setup lang="ts">
import { Asset } from 'types/assets';
import { computed } from 'vue';

interface Props {
	assetElement: Asset;
	showChevron?: boolean;
}

const props = withDefaults(defineProps<Props>(), {
	showChevron: true
});

const assetType = computed((): string => props.assetElement.assetType.name);

const yearFounded = computed((): string => props.assetElement.ventureCapitalFields?.yearFounded ?? 'N/A');

const location = computed((): string =>
	props.assetElement.realEstateAndPrivateCreditFields?.city &&
	props.assetElement.realEstateAndPrivateCreditFields.state
		? `${props.assetElement.realEstateAndPrivateCreditFields.city}, ${props.assetElement.realEstateAndPrivateCreditFields.state}`
		: 'N/A'
);

const strategy = computed((): string => props.assetElement.realEstateAndPrivateCreditFields?.strategy?.name ?? 'N/A');

const industry = computed((): string => props.assetElement.ventureCapitalFields?.industry?.name ?? 'N/A');

const contentItems = computed(
	(): Array<{ label: string; value: string; classes?: string }> =>
		props.assetElement.elementType === 'REAL_ESTATE' || props.assetElement.elementType === 'BOND'
			? [
					{
						label: 'Location',
						value: location.value
					},
					{
						label: 'Type',
						value: assetType.value
					},
					{
						label: 'Strategy',
						value: strategy.value
					}
				]
			: [
					{
						label: 'Founded',
						value: yearFounded.value,
						classes: 'narrow'
					},
					{
						label: 'Type',
						value: assetType.value
					},
					{
						label: 'Industry',
						value: industry.value
					}
				]
);

const isThumbnailSquare = computed((): boolean => !!props.assetElement.thumbnailSquare);

const imageClass = computed((): string =>
	isThumbnailSquare.value ? 'asset-element-card-image__square' : 'asset-element-card-image'
);

const name = computed((): string => props.assetElement.name ?? props.assetElement.assetType.name);

const thumbnailPhoto = computed((): string => props.assetElement.thumbnail?.url ?? '');
</script>

<style lang="scss" scoped>
@use '../../styles/constants/_colors.scss' as *;

.asset-element-card {
	&-image {
		height: 5.625rem;
		min-width: 8.125rem;
		width: 8.125rem;

		&__square {
			height: 5.625rem;
			min-width: 5.625rem;
			width: 5.625rem;
		}
	}

	.tag {
		display: inline-block;
	}

	&-desktop {
		:deep(.arrow path[pid='0']) {
			stroke: gray(40);
		}

		&:hover {
			:deep(.arrow path[pid='0']) {
				stroke: token('content-primary');
			}
		}
	}

	&__content {
		flex: 1;
		max-width: 748px;

		&-item.narrow {
			flex-basis: 20%;
		}

		&-item:not(.narrow) {
			flex: 1;
		}
	}
}
</style>
